import React, { useState, lazy, Suspense, useEffect } from "react";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { Box, CssBaseline, styled, useTheme, useMediaQuery } from "@mui/material";
import TrainerLogin from "./views/TrainerLogin";
import TrainerSidebar from "./layout/TrainerSidebar";
import LoadingModal from "../components/layout/LoadingModal";

const TrainerDashboard = lazy(() => import("./views/TrainerDashboard"));
const TrainerCommissions = lazy(() => import("./views/TrainerCommissions"));
const TrainerReferrals = lazy(() => import("./views/TrainerReferrals"));
const TrainerRedemptions = lazy(() => import("./views/TrainerRedemptions"));
const TrainerPaymentSettings = lazy(() => import("./views/TrainerPaymentSettings"));

const drawerWidth = 250;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile" })(
  ({ theme, open, isMobile }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(isMobile
      ? {
          marginLeft: 0,
        }
      : {
          marginLeft: `-${drawerWidth}px`,
          ...(open && {
            transition: theme.transitions.create("margin", {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
          }),
        }),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  })
);

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const TrainerPortal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    // Check if user is trying to access protected routes
    const path = window.location.pathname;
    const isLogin =
      path === "/affiliatorportal/login" ||
      path === "/trainerportal/login" ||
      path === "/trainer-portal/login";
    if (!isLogin) {
      const trainerToken = window.localStorage.getItem("trainerPortalToken");
      if (!trainerToken) {
        navigate("/affiliatorportal/login");
      }
    }
  }, [navigate]);

  const handleDrawerToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Update sidebar state when screen size changes
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const isLoginPage =
    location.pathname === "/affiliatorportal/login" ||
    location.pathname === "/trainerportal/login" ||
    location.pathname === "/trainer-portal/login";

  return (
    <>
      <CssBaseline />
      {isLoginPage ? (
        <Suspense fallback={<LoadingModal open message="Loading..." />}>
          <Routes>
            <Route path="/login" element={<TrainerLogin />} />
            <Route path="*" element={<TrainerLogin />} />
          </Routes>
        </Suspense>
      ) : (
        <Box
          sx={{
            display: "flex",
           // background: "#F7F7F7",
            minHeight: "100vh",
          }}
        >
          <TrainerSidebar open={sidebarOpen} onClose={handleDrawerToggle} isMobile={isMobile} />
          <Main open={sidebarOpen} isMobile={isMobile}>
            <DrawerHeader />
            <Suspense fallback={<LoadingModal open message="Loading..." />}>
              <Routes>
                <Route path="/dashboard" element={<TrainerDashboard onMenuClick={handleDrawerToggle} sidebarOpen={sidebarOpen} />} />
                <Route path="/commissions" element={<TrainerCommissions onMenuClick={handleDrawerToggle} sidebarOpen={sidebarOpen} />} />
                <Route path="/referrals" element={<TrainerReferrals onMenuClick={handleDrawerToggle} sidebarOpen={sidebarOpen} />} />
                <Route path="/redemptions" element={<TrainerRedemptions onMenuClick={handleDrawerToggle} sidebarOpen={sidebarOpen} />} />
                <Route path="/payment-settings" element={<TrainerPaymentSettings onMenuClick={handleDrawerToggle} sidebarOpen={sidebarOpen} />} />
                <Route path="*" element={<TrainerDashboard onMenuClick={handleDrawerToggle} sidebarOpen={sidebarOpen} />} />
              </Routes>
            </Suspense>
          </Main>
        </Box>
      )}
    </>
  );
};

export default TrainerPortal;
